import FileUploadFieldPlus from "@/components/ui/FileUploadFieldPlus";
import VideoPlayerV3, { DEFAULT_BLURHASH } from "@/components/ui/VideoPlayerV3";
import VideoUploaderWithPreview from "@/components/ui/VideoUploaderWithPreview";
import ConfirmationModal from "@/context/ConfirmationContext/components/confirmationModal";
import { useGlobalContext } from "@/context/GlobalContext";
import usePolling from "@/hooks/usePolling";
import {
  useGetVideoChangeStatusQuery,
  useRequestVideoChangeMutation,
} from "@/redux/feature/user/userApi";
import { TProfileVideo } from "@/redux/feature/user/userType";
import { FileDetails } from "@/types/types";
import { handleApiRequest } from "@/utils/handleApiRequest";
import { Info, X } from "lucide-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

const RequestForPrimaryVideoModal = ({
  RequestData,
  videos,
  setIsRequestPrimaryOpen,
}: {
  RequestData: any;
  videos: TProfileVideo[];
  setIsRequestPrimaryOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { userData } = useGlobalContext();
  const [creatorVideos, setCreatorVideos] = useState<TProfileVideo[]>([]);
  const [playingVideoUrl, setPlayingVideoUrl] = useState("");
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<FileDetails[]>([]);

  const requestStatus = RequestData?.data;

  const handlePlayVideo = (url: string) => {
    setPlayingVideoUrl(url === playingVideoUrl ? "" : url);
  };

  const handleVideoSelect = (videoId: string) => {
    setSelectedVideoId(videoId);
    setShowConfirmation(true);
  };
  const handleRemoveVideo = (videoUrl: string) => {
    const filtered = creatorVideos.filter(
      (item) => item.video.live_url !== videoUrl
    );
    const filteredUploadedVids = uploadedFiles.filter(
      (item) => item.live_url !== videoUrl
    );

    setUploadedFiles(filteredUploadedVids);
    setCreatorVideos(filtered);
  };
  const [requestVideoChange, { isLoading }] = useRequestVideoChangeMutation();

  const handleConfirm = async () => {
    const payload = {
      email: userData.email,
      video: creatorVideos.map((v) => ({
        ...v.video, // includes file metadata like live_url, key, etc.
        thumbnail: v.thumbnail,
        hash: v.hash,
      })),
      requestedVideoUrl: selectedVideoId, //selected video url
    };

    await handleApiRequest({
      requestFunction: requestVideoChange,
      payload: payload,
      onSuccess: (res) => {
        console.log(res);
        toast.success("Submitted Request");
        setShowConfirmation(false);
        setIsRequestPrimaryOpen(false);
      },
      onError: (err) => {
        setIsRequestPrimaryOpen(false);
        toast.error(err?.message || "Some thing went wrong");
        setShowConfirmation(false);
        console.log(err);
      },
    });
  };

  const requestPending = requestStatus?.status === "pending";
  
  useEffect(() => {
    setCreatorVideos(videos);
  }, []);

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-xl font-medium text-gray-900 mb-2">
          Request for Primary Video
        </h3>
        <p className="text-gray-600">
          Choose which video you want to display as your primary video
        </p>
      </div>

      {requestPending && (
        <div className="py-4  flex text-gray-600  gap-x-2 items-start  px-3 border border-gray-200  rounded-[12px] mb-7 text-medium text-lg ">
          <div className="mt-0.5">
            <Info size={18} />
          </div>
          <p className="text-[18px] leading-[24px]">
            A Pending request is already there! Please wait until that request
            processes!
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-6">
        {!requestPending && (
          <>
            {creatorVideos?.length <= 2 && (
              <VideoUploaderWithPreview
                showSelectedFiles={false}
                showUploadAnimationOnly={true}
                height="290px"
                width="165px"
                cardDesignSize=" h-[290px] w-[165px]"
                accept={{
                  "video/mp4": [],
                  "video/webm": [],
                  "video/ogg": [],
                }}
                maxFiles={3}
                useLiveUrl={false}
                useExternalState={true}
                externalFiles={uploadedFiles}
                setExternalFiles={setUploadedFiles}
                onFilesUploaded={(uploaded) => {
                  const newVideos: TProfileVideo[] = uploaded.map((file) => ({
                    video: file,
                    thumbnail: file?.thumbnail || "",
                    hash: file?.hash as string,
                    isActive: false,
                  }));
                  setCreatorVideos((prev) => [...newVideos, ...prev]);
                }}
              />
            )}
          </>
        )}
        {creatorVideos?.map((item, i) => (
          <div
            key={item?.hash}
            className="group  h-[290px]  w-[165px]   relative"
          >
            <div className="relative mb-3">
              {/* Labels */}
              {item.isActive && (
                <div className="absolute top-3 left-3 z-50 bg-emerald-600 text-white text-xs font-medium px-2 py-1 rounded-full shadow-sm">
                  Current Primary
                </div>
              )}
              {!requestPending && !item.isActive && (
                <div
                  onClick={() =>
                    handleRemoveVideo(item?.video?.live_url as string)
                  }
                  className="absolute cursor-pointer top-3 -right-2 z-50 bg-blue-600  text-white text-xs font-medium p-1 rounded-full shadow-sm"
                >
                  <X size={"14px"} />
                </div>
              )}

              <div className="relative h-[290px] w-[180px] bg-gray-50 rounded-xl overflow-hidden border border-gray-100 group-hover:border-gray-200 transition-all duration-300 group-hover:shadow-md">
                <VideoPlayerV3
                  url={item.video.live_url as string}
                  thumbnailUrl={item.thumbnail}
                  playing={playingVideoUrl === item.video.live_url}
                  muted={false}
                  blurhash={item?.hash || DEFAULT_BLURHASH}
                  onPlay={() => handlePlayVideo(item.video.live_url as string)}
                  border="rounded-lg"
                />
              </div>
            </div>

            {/* Action Button */}
            {!requestPending && (
              <button
                onClick={() =>
                  handleVideoSelect(item?.video?.live_url as string)
                }
                disabled={item.isActive}
                className={`
                w-full py-2 rounded-lg absolute bottom-2 z-30 left-2 text-sm font-medium transition-all duration-200
                ${
                  item.isActive
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                    : "bg-gray-900 text-white hover:bg-gray-800 active:bg-gray-950 shadow-sm hover:shadow-md"
                }
              `}
              >
                {item.isActive ? "Already Primary" : "Make Primary"}
              </button>
            )}
          </div>
        ))}
      </div>

      <ConfirmationModal
        isOpen={showConfirmation}
        setOpen={setShowConfirmation}
        onConfirm={handleConfirm}
        title="Confirm Primary Video"
      />
    </div>
  );
};

export default RequestForPrimaryVideoModal;
